import { useRef, useEffect, useCallback } from 'react';
import CreateApi from '../../shared/Settings/appSettings';
import { useTBContext } from '../Contexts/useTBStates';
import type { ViewEventData, TbRegistration, CourseTeeSetData, EventScoreWithId, TbEventWithId } from '@moment-golf/types';
import { normalizeEventRounds } from '../../shared/validations/validateEvent';

export interface OpenEventOptions {
    /** Keep the current collection open and stay in the groups section. */
    preserveCollection?: boolean;
}

export function useOpenEvent() {
    const api = CreateApi();

    const {
        eventData,
        setEventData,
        setViewEvent,
        setViewEventData,
        setViewGroup,
        originalViewEvent,
        originalRegistrations,
        originalViewEventData,
    } = useTBContext();

    const eventDataRef = useRef(eventData);
    useEffect(() => {
        eventDataRef.current = eventData;
    }, [eventData]);

    const openEvent = useCallback(
        async (event: TbEventWithId, options?: OpenEventOptions) => {
            const opts = options ?? {};

            const viewEventDataPassed: ViewEventData = {
                registrations: [],
                scores: [],
                courseData: [],
            };
            let otherCourseIDs: string[] = [];

            const selectedEvent =
                eventDataRef.current.find((refEvent: TbEventWithId) => refEvent._id === event._id) ?? event;
            const dataDate =
                selectedEvent && Object.keys(selectedEvent).length > 20
                    ? new Date(new Date(selectedEvent.modifiedAt).getTime() + 60 * 1000)
                    : null;

            await api
                .get(`/tb/GetEventDetails`, {
                    params: {
                        _id: selectedEvent._id,
                        referenceDate: dataDate,
                    },
                })
                .then((res: { data: TbEventWithId | null }) => {
                    const eventDataRes = res.data;
                    const updatedEventData = [...eventDataRef.current];

                    if (eventDataRes) {
                        const existingEventIndex = updatedEventData.findIndex(
                            (e: TbEventWithId) => e._id === eventDataRes._id
                        );
                        if (existingEventIndex !== -1) {
                            updatedEventData[existingEventIndex] = {
                                ...updatedEventData[existingEventIndex],
                                ...eventDataRes,
                            };
                        } else {
                            updatedEventData.push(eventDataRes);
                        }

                        const eventToView = updatedEventData.find(
                            (e: TbEventWithId) => e._id === eventDataRes._id
                        );

                        if (eventToView) {
                            const normalizedEvent = normalizeEventRounds(eventToView) ?? eventToView;
                            const eventIndex = updatedEventData.findIndex((e: TbEventWithId) => e._id === eventToView._id);
                            if (eventIndex !== -1) updatedEventData[eventIndex] = normalizedEvent;
                            otherCourseIDs = (normalizedEvent.rounds ?? [])
                                .filter(
                                    (round: any) =>
                                        round.courseID !== normalizedEvent.courseID &&
                                        round.courseID !== null
                                )
                                .map((round: any) => round.courseID);
                            setEventData(updatedEventData);
                            setViewEvent(normalizedEvent);
                            originalViewEvent.current = JSON.parse(JSON.stringify(normalizedEvent));
                        }
                    } else if (
                        !eventDataRes &&
                        selectedEvent &&
                        Object.keys(selectedEvent).length > 20
                    ) {
                        const normalizedEvent = normalizeEventRounds(selectedEvent) ?? selectedEvent;
                        otherCourseIDs = (normalizedEvent.rounds ?? [])
                            .filter(
                                (round: any) =>
                                    round.courseID !== normalizedEvent.courseID &&
                                    round.courseID !== null
                            )
                            .map((round: any) => round.courseID as string);
                        setViewEvent(normalizedEvent);
                        originalViewEvent.current = JSON.parse(JSON.stringify(normalizedEvent));
                    }
                });

            await api
                .get(`/tb/GetEventRegistrations`, {
                    params: { _id: event._id },
                })
                .then((res: { data: TbRegistration[] }) => {
                    viewEventDataPassed.registrations = res.data;
                })
                .catch((err: any) => {
                    console.log('Error fetching registration data', err);
                });

            const courseIDs = [event.courseID, ...otherCourseIDs];
            await api
                .get(`/tb/GetCourseTeesets`, {
                    params: { _ids: courseIDs },
                })
                .then((res: { data: CourseTeeSetData[] }) => {
                    viewEventDataPassed.courseData = res.data;
                })
                .catch((err: any) => {
                    console.log('Error fetching teesets', err);
                });

            await api
                .get(`/tb/GetEventScores`, {
                    params: { _id: event._id },
                })
                .then((res: { data: EventScoreWithId[] }) => {
                    viewEventDataPassed.scores = res.data;
                })
                .catch((err: any) => {
                    console.log('Error fetching scores', err);
                });

            setViewEventData(viewEventDataPassed);
            originalRegistrations.current = JSON.parse(
                JSON.stringify(viewEventDataPassed.registrations)
            );
            originalViewEventData.current = JSON.parse(JSON.stringify(viewEventDataPassed));

            // Opening from the tournaments list clears any leftover collection context.
            // Opening from a collection keeps viewGroup so breadcrumbs stay nested.
            if (!opts.preserveCollection) {
                setViewGroup(null);
            }
        },
        [
            api,
            setEventData,
            setViewEvent,
            setViewEventData,
            setViewGroup,
            originalViewEvent,
            originalRegistrations,
            originalViewEventData,
        ]
    );

    return openEvent;
}
